<template>
  <section class="network-payors">
    <div class="page-header">
      <h3>
        Network Payors
        <span class="lab-name" v-if="labName">— {{ labName }}</span>
      </h3>
      <p class="sub">
        Payors this lab holds a contract with, and the states each contract covers.
      </p>
    </div>

    <div class="summary" v-if="networkPayors">
      <div class="stat in">
        <span class="count">{{ inNetworkPayors.length }}</span>
        <span class="label">In network</span>
      </div>
      <div class="stat out">
        <span class="count">{{ outOfNetworkPayors.length }}</span>
        <span class="label">Out of network</span>
      </div>
      <div class="stat types" v-if="resultedCaseTypes.length">
        <span class="count">{{ resultedCaseTypes.length }}</span>
        <span class="label">Case types resulted</span>
      </div>
    </div>

    <div class="filters">
      <el-input
        v-model="search"
        placeholder="Search payor or state"
        clearable
        size="small"
        class="search"
      />
      <el-radio-group v-model="networkFilter" size="small">
        <el-radio-button label="ALL">All</el-radio-button>
        <el-radio-button label="IN_NETWORK">In network</el-radio-button>
        <el-radio-button label="OUT_OF_NETWORK">Out of network</el-radio-button>
      </el-radio-group>
    </div>

    <el-table
      :data="filteredPayors"
      v-loading="networkPayorsLoading"
      border
      stripe
      empty-text="No payor contracts found for this lab"
    >
      <el-table-column label="Payor" prop="payor" sortable min-width="200">
        <template #default="scope">
          <span class="payor">{{ scope.row.payor }}</span>
          <!-- The contract spelling, shown only when it differs from the matched name -->
          <span class="raw" v-if="scope.row.payor_raw !== scope.row.payor">
            {{ scope.row.payor_raw }}
          </span>
        </template>
      </el-table-column>

      <el-table-column label="State" prop="state" sortable width="110">
        <template #default="scope">
          <el-tag size="small" :type="scope.row.state === 'GLOBAL' ? 'info' : ''">
            {{ scope.row.state }}
          </el-tag>
        </template>
      </el-table-column>

      <el-table-column label="Network" prop="network_type" sortable width="150">
        <template #default="scope">
          <el-tag
            size="small"
            :type="scope.row.network_type === 'IN_NETWORK' ? 'success' : 'danger'"
          >
            {{ scope.row.network_type === "IN_NETWORK" ? "In network" : "Out of network" }}
          </el-tag>
        </template>
      </el-table-column>

      <el-table-column label="Effective" width="130">
        <template #default="scope">{{ formatDate(scope.row.effective_date) }}</template>
      </el-table-column>

      <el-table-column label="Revalidation" width="140">
        <template #default="scope">{{ formatDate(scope.row.revalidation_date) }}</template>
      </el-table-column>
    </el-table>
  </section>
</template>

<script>
import { mapGetters } from "vuex";
import moment from "moment";

export default {
  name: "NetworkPayors",
  data() {
    return {
      search: "",
      networkFilter: "ALL",
    };
  },
  computed: {
    ...mapGetters("networkPayors", [
      "getNetworkPayors",
      "getNetworkPayorsLoading",
    ]),
    networkPayors() {
      return this.getNetworkPayors;
    },
    networkPayorsLoading() {
      return this.getNetworkPayorsLoading;
    },
    labName() {
      return this.networkPayors?.lab_name || this.networkPayors?.lab_code || "";
    },
    allPayors() {
      return this.networkPayors?.insurances || [];
    },
    inNetworkPayors() {
      return this.allPayors.filter((p) => p.network_type === "IN_NETWORK");
    },
    outOfNetworkPayors() {
      return this.allPayors.filter((p) => p.network_type === "OUT_OF_NETWORK");
    },
    resultedCaseTypes() {
      return this.networkPayors?.case_types || [];
    },
    filteredPayors() {
      const term = this.search.trim().toLowerCase();
      return this.allPayors
        .filter(
          (p) =>
            this.networkFilter === "ALL" || p.network_type === this.networkFilter
        )
        .filter((p) => {
          if (!term) return true;
          return (
            (p.payor || "").toLowerCase().includes(term) ||
            (p.payor_raw || "").toLowerCase().includes(term) ||
            (p.state || "").toLowerCase().includes(term)
          );
        })
        .sort(
          (a, b) =>
            (a.payor || "").localeCompare(b.payor || "") ||
            (a.state || "").localeCompare(b.state || "")
        );
    },
  },
  async mounted() {
    await this.$store.dispatch("networkPayors/fetchNetworkPayors");
  },
  methods: {
    formatDate(value) {
      return value ? moment(value).format("MM/DD/YYYY") : "--";
    },
  },
};
</script>

<style lang="scss" scoped>
.network-payors {
  padding: 20px;

  .page-header {
    margin-bottom: 16px;

    h3 {
      margin: 0;
      font-size: 20px;

      .lab-name {
        font-weight: 400;
        color: #606266;
      }
    }

    .sub {
      margin: 4px 0 0;
      color: #909399;
      font-size: 13px;
    }
  }

  .summary {
    display: flex;
    gap: 12px;
    margin-bottom: 16px;

    .stat {
      border: 1px solid #ebeef5;
      border-radius: 6px;
      padding: 10px 18px;
      min-width: 130px;

      .count {
        display: block;
        font-size: 22px;
        font-weight: 600;
      }

      .label {
        font-size: 12px;
        color: #909399;
      }

      &.in .count {
        color: #67c23a;
      }

      &.out .count {
        color: #f56c6c;
      }
    }
  }

  .filters {
    display: flex;
    gap: 12px;
    align-items: center;
    margin-bottom: 12px;

    .search {
      max-width: 280px;
    }
  }

  .payor {
    font-weight: 500;
  }

  .raw {
    display: block;
    font-size: 11px;
    color: #909399;
  }
}
</style>
